import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import usePageTitle from "@/hooks/usePageTitle";
import {
  ChevronDown, Brain, FileText, Users, Star,
  Factory, Car, Landmark, Monitor, ShoppingBag, Heart,
  Package, Hotel, GraduationCap, Truck, Phone, Building2,
} from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1 } };

/* ───── Section 1: Hero ───── */
function Hero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center bg-navy overflow-hidden" data-testid="hero-section">
      {/* Dot Pattern */}
      <div className="absolute inset-0 opacity-[0.05]" style={{
        backgroundImage: "radial-gradient(#C9A84C 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }} />
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
          className="inline-block border border-gold text-gold font-dm text-sm px-5 py-2 rounded-full mb-8"
        >
          AI-Powered &middot; Built for HR Leaders &middot; Trusted Globally
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
          className="font-playfair text-white text-4xl sm:text-5xl lg:text-[56px] leading-tight font-bold mb-6"
        >
          Build Smarter Teams.<br />Map Every Role. Grow Every Career.
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}
          className="font-dm text-white/70 text-lg max-w-[600px] mx-auto mb-10"
        >
          RoleIQ generates 3-tier competency frameworks aligned to your company values, creates branded job descriptions, and connects you to a professional growth community — in 30 seconds.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center mb-6"
        >
          <Link to="/generate" className="bg-gold text-navy font-dm font-bold px-8 py-4 rounded-lg hover:bg-gold/90 transition-colors text-base" data-testid="hero-cta-generate">
            Generate Your First Map Free
          </Link>
          <Link to="/how-it-works" className="border border-white text-white font-dm px-8 py-4 rounded-lg hover:bg-white/10 transition-colors text-base">
            Watch How It Works
          </Link>
        </motion.div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
          className="font-dm text-white/50 text-sm"
        >
          No login required &middot; Works for any industry &middot; Trusted by HR professionals
        </motion.p>
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.5 }} className="mt-12">
          <ChevronDown className="w-8 h-8 text-gold mx-auto" />
        </motion.div>
      </div>
    </section>
  );
}

/* ───── Section 2: Metrics ───── */
const metrics = [
  { num: "500+", label: "Roles Mapped" },
  { num: "50+", label: "Industries Covered" },
  { num: "3-Tier", label: "SKA Framework" },
  { num: "30 Sec", label: "To Generate" },
];

function Metrics() {
  return (
    <section className="bg-white py-16" data-testid="metrics-section">
      <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8">
        {metrics.map((m, i) => (
          <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible"
            viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
            className="text-center"
          >
            <p className="font-playfair text-navy text-4xl font-bold">{m.num}</p>
            <p className="font-dm text-gold text-sm mt-1 uppercase tracking-wider">{m.label}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ───── Section 3: Problem ───── */
const painPoints = [
  "Generic templates that don't fit your company culture",
  "No connection between company values and role skills",
  "Job descriptions that don't reflect what the role actually needs",
];

function Problem() {
  return (
    <section className="bg-cream py-20" data-testid="problem-section">
      <div className="max-w-5xl mx-auto px-4">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="font-playfair text-navy text-3xl sm:text-4xl text-center font-semibold mb-12"
        >
          HR teams waste weeks building competency frameworks manually
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {painPoints.map((text, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.5 }}
              className="bg-white border-l-4 border-gold rounded-xl shadow-md p-6"
            >
              <p className="font-dm text-navy">{text}</p>
            </motion.div>
          ))}
        </div>
        <motion.p variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-center font-dm text-gold font-bold text-lg"
        >
          RoleIQ solves all three in 30 seconds
        </motion.p>
      </div>
    </section>
  );
}

/* ───── Section 4: Features ───── */
const features = [
  { icon: Brain, title: "AI Competency Mapping", desc: "Generate 3-tier SKA frameworks — Company Value \u2192 Role Competency \u2192 Observable Skills — aligned to your culture in 30 seconds" },
  { icon: FileText, title: "Branded Job Descriptions", desc: "Professional JDs with your company name, values, and competencies embedded — ready to post in minutes" },
  { icon: Users, title: "Professional Community", desc: "Stories, forums, mentoring, and a dedicated space for women at work — grow together with professionals across all industries" },
];

function Features() {
  return (
    <section className="bg-white py-20" data-testid="features-section">
      <div className="max-w-5xl mx-auto px-4 text-center">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="font-playfair text-navy text-3xl sm:text-4xl font-semibold mb-3"
        >
          Everything your HR team needs
        </motion.h2>
        <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.1 }}
          className="font-dm text-navy/50 mb-12"
        >
          One platform. Three powerful capabilities.
        </motion.p>
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.5 }}
              className="bg-white border border-gold/20 rounded-xl shadow-md p-8 hover:shadow-lg hover:-translate-y-1 transition-all"
            >
              <div className="w-14 h-14 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <f.icon className="w-7 h-7 text-gold" />
              </div>
              <h3 className="font-playfair text-navy text-xl font-semibold mb-3">{f.title}</h3>
              <p className="font-dm text-navy/60 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Section 5: How It Works ───── */
const steps = [
  { num: "1", title: "Enter Details", desc: "Add your company name, values, role, and level" },
  { num: "2", title: "AI Generates", desc: "Claude AI builds your complete 3-tier competency framework" },
  { num: "3", title: "Download", desc: "Export premium Excel checklist and branded Job Description" },
  { num: "4", title: "Apply", desc: "Use for hiring, performance reviews, and team development" },
];

function HowItWorks() {
  return (
    <section className="bg-cream py-20" data-testid="how-it-works-section">
      <div className="max-w-5xl mx-auto px-4 text-center">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="font-playfair text-navy text-3xl sm:text-4xl font-semibold mb-14"
        >
          From details to framework in 4 steps
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connecting line (desktop) */}
          <div className="hidden lg:block absolute top-8 left-[12%] right-[12%] h-[2px] border-t-2 border-dashed border-gold/40" />
          {steps.map((s, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.5 }}
              className="relative flex flex-col items-center"
            >
              <div className="w-16 h-16 bg-white border-2 border-gold rounded-full flex items-center justify-center mb-4 z-10">
                <span className="font-playfair text-gold text-2xl font-bold">{s.num}</span>
              </div>
              <h4 className="font-playfair text-navy font-semibold text-lg mb-2">{s.title}</h4>
              <p className="font-dm text-navy/60 text-sm">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Section 6: Industries ───── */
const industries = [
  { icon: Factory, name: "Manufacturing" }, { icon: Car, name: "Automotive" },
  { icon: Landmark, name: "Banking" }, { icon: Monitor, name: "IT/Technology" },
  { icon: ShoppingBag, name: "Retail" }, { icon: Heart, name: "Healthcare" },
  { icon: Package, name: "FMCG" }, { icon: Hotel, name: "Hospitality" },
  { icon: GraduationCap, name: "Education" }, { icon: Truck, name: "Logistics" },
  { icon: Phone, name: "Telecom" }, { icon: Building2, name: "Real Estate" },
];

function Industries() {
  return (
    <section className="bg-white py-20" data-testid="industries-section">
      <div className="max-w-5xl mx-auto px-4 text-center">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="font-playfair text-navy text-3xl sm:text-4xl font-semibold mb-12"
        >
          Built for every industry
        </motion.h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {industries.map((ind, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.4 }}
              className="flex flex-col items-center p-4 rounded-xl hover:bg-gold/10 transition-colors cursor-default"
            >
              <ind.icon className="w-8 h-8 text-gold mb-2" />
              <span className="font-dm text-navy text-sm">{ind.name}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Section 7: Testimonials ───── */
const testimonials = [
  { quote: "RoleIQ helped us build competency frameworks for 200+ roles in just 2 weeks. What used to take months now takes minutes.", name: "Priya Sharma", role: "CHRO", company: "TechVista Solutions" },
  { quote: "The 3-tier SKA model is exactly what we needed. It aligns our hiring process with our company values perfectly.", name: "Rahul Mehta", role: "L&D Director", company: "Pinnacle Manufacturing" },
  { quote: "Finally, a tool that understands the Indian HR landscape. The quality of output is better than what consultants deliver.", name: "Anita Verma", role: "HR Manager", company: "NextGen Banking" },
];

function Testimonials() {
  return (
    <section className="bg-cream py-20" data-testid="testimonials-section">
      <div className="max-w-5xl mx-auto px-4">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="font-playfair text-navy text-3xl sm:text-4xl text-center font-semibold mb-12"
        >
          Trusted by HR professionals across India
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((t, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.5 }}
              className="bg-white rounded-xl shadow-md p-8"
            >
              <span className="font-playfair text-gold text-5xl leading-none">&ldquo;</span>
              <p className="font-dm text-navy/80 italic text-sm leading-relaxed mb-6">{t.quote}</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-navy/10 rounded-full" />
                <div>
                  <p className="font-dm text-navy font-semibold text-sm">{t.name}</p>
                  <p className="font-dm text-navy/50 text-xs">{t.role}, {t.company}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Section 8: Community Preview ───── */
function CommunityPreview() {
  return (
    <section className="bg-navy py-20" data-testid="community-section">
      <div className="max-w-5xl mx-auto px-4 text-center">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="font-playfair text-white text-3xl sm:text-4xl font-semibold mb-3"
        >
          More than a tool — a community
        </motion.h2>
        <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.1 }}
          className="font-dm text-white/70 mb-12 max-w-lg mx-auto"
        >
          Connect with professionals, share your story, and find your next mentor
        </motion.p>
        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {[
            { icon: Users, title: "RoleIQ Community", desc: "Stories, forums, coaching, and peer learning for all working professionals", btn: "Explore Community", path: "/community" },
            { icon: Star, title: "She Leads", desc: "A dedicated space for women and mothers in the workforce to keep growing", btn: "Join She Leads", path: "/she-leads" },
          ].map((card, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible"
              viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.5 }}
              className="border border-gold bg-white/5 rounded-xl p-8"
            >
              <card.icon className="w-10 h-10 text-gold mx-auto mb-4" />
              <h3 className="font-playfair text-white text-xl font-semibold mb-3">{card.title}</h3>
              <p className="font-dm text-white/60 text-sm mb-6">{card.desc}</p>
              <Link to={card.path} className="border border-gold text-gold font-dm text-sm px-6 py-2.5 rounded-lg hover:bg-gold/10 transition-colors inline-block">
                {card.btn}
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Section 9: Final CTA ───── */
function FinalCTA() {
  return (
    <section className="bg-gold py-20" data-testid="cta-section">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="font-playfair text-navy text-3xl sm:text-4xl font-bold mb-4"
        >
          Your first competency map is free.
        </motion.h2>
        <motion.p variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.1 }}
          className="font-dm text-navy mb-8"
        >
          No setup. No hassle. Just 30 seconds.
        </motion.p>
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: 0.2 }}>
          <Link to="/generate"
            className="inline-block bg-navy text-white font-dm font-bold px-10 py-4 rounded-lg text-lg hover:shadow-lg hover:scale-105 transition-all"
          >
            Generate Now
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ───── HomePage ───── */
export default function HomePage() {
  usePageTitle("AI-Powered HR Intelligence");
  return (
    <div>
      <Hero />
      <Metrics />
      <Problem />
      <Features />
      <HowItWorks />
      <Industries />
      <Testimonials />
      <CommunityPreview />
      <FinalCTA />
    </div>
  );
}
